import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Scale } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("admin@ehinc.co.za");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      toast.success("Welcome back");
      navigate(u?.must_change_password ? "/change-password" : "/admin");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background grain grid lg:grid-cols-2">
      <div className="hidden lg:block bg-zinc-900 text-white p-16 relative">
        <Link to="/" className="flex items-center gap-2">
          <div className="h-9 w-9 bg-white text-zinc-900 grid place-items-center">
            <Scale className="h-5 w-5" strokeWidth={1.5} />
          </div>
          <div className="leading-tight">
            <div className="font-serif text-xl font-semibold">EH Inc</div>
            <div className="text-[10px] tracking-[0.25em] uppercase text-zinc-400">Attorneys</div>
          </div>
        </Link>
        <div className="absolute bottom-16 left-16 right-16">
          <div className="text-overline text-zinc-400">Practice console</div>
          <h2 className="mt-3 font-serif text-5xl font-medium leading-[1] tracking-tighter">
            Manage matters,
            <br />
            without the admin.
          </h2>
          <p className="mt-6 text-zinc-400 text-sm leading-relaxed max-w-md">
            AI-opened intake, Kanban workflow, draft documents, simulated client comms — all in one console.
          </p>
        </div>
      </div>
      <div className="flex items-center justify-center px-6 py-16">
        <div className="w-full max-w-sm">
          <h1 className="font-serif text-4xl font-medium tracking-tight">Lawyer login</h1>
          <p className="mt-2 text-sm text-zinc-600">Sign in to your EH Inc dashboard.</p>
          <form onSubmit={submit} className="mt-10 space-y-5">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="mt-2 rounded-none"
                data-testid="login-email"
              />
            </div>
            <div>
              <Label htmlFor="pw">Password</Label>
              <Input
                id="pw"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="mt-2 rounded-none"
                data-testid="login-password"
              />
            </div>
            {error && <div className="text-sm text-red-600" data-testid="login-error">{error}</div>}
            <Button
              type="submit"
              disabled={loading}
              className="w-full rounded-none bg-zinc-900 hover:bg-zinc-800"
              data-testid="login-submit"
            >
              {loading ? "Signing in…" : "Sign in →"}
            </Button>
          </form>
          <p className="mt-8 text-xs text-zinc-500">
            Trouble logging in? Contact your administrator. Default admin: <code className="font-mono">admin@ehinc.co.za</code>
          </p>
          <Link to="/" className="mt-6 inline-block text-sm text-zinc-500 hover:text-zinc-900">← Back to website</Link>
        </div>
      </div>
    </div>
  );
}
